import React, {useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import Helmet from "react-helmet";

import {ReactComponent as BackIcon} from "../../../assets/back.svg";
import Box from "../../Interface/Box";
import Separator from "../../Interface/Separator/Separator";
import {useEthereum} from "../../../EthereumContext";
import Input from "../../Interface/Input/Input";
import {clearProposal, setDescription, setTitle} from "../../../redux/proposal";
import Button from "../../Interface/Button/Button";
import AddActionModal from "../ActionModal";
import ActionRow from "../ActionRow";
import {encodeParameters} from "../../../utils";
import Account from "../../Account/Account";
import {toast} from "react-toastify";
import './style.scss';
import useResize from "../../../hooks/useResize";

function NewProposal() {
    const {governor} = useEthereum();
    const history = useHistory();

    const proposal = useSelector(store => store.proposal);
    const dispatch = useDispatch();

    const [actionsRevealed, setActionsRevealed] = React.useState(false);
    const [addActionOpen, setAddActionOpen] = React.useState(false);
    const [pending, setPending] = React.useState(false);

    const submitProposal = async () => {
        setPending(true);

        try {
            const targets = proposal.actions.map(a => a.target);
            const signatures = proposal.actions.map(a => a.signature);
            const values = proposal.actions.map(a => a.value);
            const callDatas = proposal.actions.map(a => {
                const signatueRegex = /(.+)\((.+)?\)/;
                const splitSignature = a.signature.match(signatueRegex);
                const parameterTypes = splitSignature[2] ? splitSignature[2].split(',') : [];
                return encodeParameters(parameterTypes, a.parameters);
            });

            const fullDescription = proposal.title + "\n" + proposal.description;

            const tx = await governor.propose(targets, values, signatures, callDatas, fullDescription);
            await tx.wait();
            toast.info("Proposal submission is completed");

            dispatch(clearProposal());
            history.push("/voting");
        } catch(err) {
            toast.error("Proposal submission failed");
        } finally {
            setPending(false);
        }
    }

    const { windowWidth } = useResize();

    const mobileView = useMemo(() => windowWidth <= 600, [windowWidth]);

    return (
        <div className="new-proposal">
            <Helmet>
                <title>Create Proposal - Roobee DAO</title>
            </Helmet>
            <div style={{display: "flex", justifyContent: "space-between", width: "100%", height: 55}}>
                <div style={{display: "flex", alignItems: "center", cursor: "pointer"}} onClick={() => history.goBack()}>
                    <BackIcon />
                    <span style={{marginLeft: 10, fontSize: 14, color: "var(--grey1)", fontWeight: 600}}>Back</span>
                </div>
                { mobileView ? null : <Account /> }
            </div>
            <h2>Create Proposal</h2>
            <Box direction="column" align="center" style={{marginTop: 40}}>
                <span style={{fontWeight: 500, width: "100%"}}>Fill in the information</span>
                <Separator />
                <div className="new-proposal-wrapper-inputs">
                    <h3 style={{textAlign: "center"}}>Proposal Description</h3>
                    <span style={{color: "var(--grey0)", fontWeight: 600}}>Title</span>
                    <Input
                        style={{marginTop: 5, marginBottom: 20}}
                        placeholder="Title of proposal"
                        value={proposal.title}
                        setValue={(v) => dispatch(setTitle(v))}
                    />
                    <span style={{color: "var(--grey0)", fontWeight: 600}}>Overview</span>
                    <Input
                        textarea
                        style={{marginTop: 5}}
                        placeholder="Why should people vote on your proposal?"
                        value={proposal.description}
                        setValue={(v) => dispatch(setDescription(v))}
                    />
                    {actionsRevealed ? (
                        <React.Fragment>
                            <h3
                                style={{textAlign: "center", marginTop: 40, cursor: "pointer"}}
                                onClick={() => setActionsRevealed(false)}
                            >
                                Actions ↑
                            </h3>
                            {proposal.actions.map((action, index) => (
                                <ActionRow key={index} action={action} index={index} />
                            ))}
                            <div
                                style={{
                                    borderRadius: 8,
                                    height: 70,
                                    maxHeight: 70,
                                    boxSizing: "border-box",
                                    border: "2px solid var(--turquoiseDark)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: 20,
                                    cursor: "pointer",
                                    color: "var(--turquoiseDark)",
                                    fontWeight: 600
                                }}
                                onClick={() => setAddActionOpen(true)}
                            >
                                <span>Add action</span>
                                <span>+</span>
                            </div>
                            {addActionOpen && <AddActionModal isOpen={addActionOpen} setIsOpen={setAddActionOpen} />}
                        </React.Fragment>
                    ) : (
                        <h3
                            style={{textAlign: "center", marginTop: 40, cursor: "pointer"}}
                            onClick={() => setActionsRevealed(true)}
                        >
                            Actions ↓
                        </h3>
                    )}

                    <div style={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%", marginTop: 40}}>
                        <Button
                            className="highlightedButton"
                            disabled={proposal.title === "" || proposal.description === "" || pending}
                            blinking={pending}
                            onClick={submitProposal}
                        >
                            Submit proposal
                        </Button>
                    </div>
                </div>
            </Box>
        </div>
    );
}

export default NewProposal;
